/**
 * JobResultCard Component
 * Displays job results with side-by-side image comparison and stats
 * Used in chat interface for specific job queries and job completion results
 */
import React, { useState } from "react";
import { ImageComparisonModal } from "./ImageComparisonModal";
import { getFileUrl } from "../../utils";

interface JobResultCardProps {
  job: {
    id: string;
    job_number?: string | number;
    job_type: string;
    status: string;
    input_file_path?: string;
    file_url?: string;           // URL for input file (from API)
    input_preview?: string;      // Blob URL from chat upload (preferred!)
    annotated_output_path?: string;
    annotated_output_url?: string; // URL for annotated output (from API)
    results?: Record<string, any>;
    completed_at?: string;
  };
  showImages?: boolean; // false for batch queries, true for specific job
}

export const JobResultCard: React.FC<JobResultCardProps> = ({
  job,
  showImages = true,
}) => {
  const [isModalOpen, setIsModalOpen] = useState(false);

  // For input image: PREFER input_preview (blob URL from chat upload) 
  // over file_url/input_file_path (which requires server fetch)
  const inputImageUrl = job.input_preview || getFileUrl(job.file_url || job.input_file_path);
  const outputImageUrl = getFileUrl(job.annotated_output_url || job.annotated_output_path);
  
  // Debug logging
  console.log("🖼️ JobResultCard URLs:", {
    input_preview: job.input_preview,
    file_url: job.file_url,
    computed_input: inputImageUrl,
    computed_output: outputImageUrl
  });


  // Extract stats from results
  const results = job.results || {};
  const stats: { label: string; value: string | number }[] = [];

  // For image-based people counting, 'people_count' is the key metric
  if (results.people_count !== undefined) {
    stats.push({ label: "People Detected", value: results.people_count });
  }
  // For video-based jobs
  if (results.total_people_detected !== undefined) {
    stats.push({ label: "Total People Detected", value: results.total_people_detected });
  }
  if (results.total_plates_detected !== undefined) {
    stats.push({ label: "Plates Detected", value: results.total_plates_detected });
  }
  if (results.total_detections !== undefined) {
    stats.push({ label: "Total Detections", value: results.total_detections });
  }
  if (results.plate_details?.length) {
    const plates = results.plate_details.map((p: any) => p.plate_number).join(", ");
    stats.push({ label: "Plate Numbers", value: plates || "N/A" });
  }
  if (results.average_people_count !== undefined) {
    stats.push({ label: "Avg People Count", value: results.average_people_count });
  }
  if (results.frame_count !== undefined) {
    stats.push({ label: "Frames Processed", value: results.frame_count });
  }


  const jobTypeName = job.job_type.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());

  return (
    <>
      <div className="bg-slate-700/50 rounded-xl p-4 border border-slate-600 max-w-lg">
        {/* Header */}
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-2">
            <span className="text-2xl">
              {job.status === "completed" ? "✅" : job.status === "failed" ? "❌" : "⏳"}
            </span>
            <div>
              <h4 className="text-white font-semibold">{jobTypeName}</h4>
              {job.job_number && (
                <p className="text-slate-400 text-xs">Job #{job.job_number}</p>
              )}
            </div>
          </div>
          <span
            className={`px-2 py-1 rounded text-xs font-medium ${
              job.status === "completed"
                ? "bg-green-500/20 text-green-300"
                : job.status === "failed"
                ? "bg-red-500/20 text-red-300"
                : "bg-yellow-500/20 text-yellow-300"
            }`}
          >
            {job.status}
          </span>
        </div>

        {/* Side-by-side images (only for specific job queries) */}
        {showImages && (inputImageUrl || outputImageUrl) && (
          <div
            className="grid grid-cols-2 gap-2 mb-3 cursor-pointer hover:opacity-90 transition-opacity"
            onClick={() => setIsModalOpen(true)}
          >
            {/* Input thumbnail */}
            <div className="bg-slate-800 rounded-lg p-1 flex flex-col items-center">
              <span className="text-xs text-blue-300 mb-1">Input</span>
              {inputImageUrl ? (
                <img
                  src={inputImageUrl}
                  alt="Input"
                  className="w-full h-24 object-cover rounded"
                  onError={(e) => {
                    e.currentTarget.style.display = "none";
                  }}
                />
              ) : (
                <div className="w-full h-24 bg-slate-700 rounded flex items-center justify-center text-slate-500 text-xs">
                  No image
                </div>
              )}
            </div>

            {/* Output thumbnail */}
            <div className="bg-slate-800 rounded-lg p-1 flex flex-col items-center">
              <span className="text-xs text-green-300 mb-1">Output</span>
              {outputImageUrl ? (
                <img
                  src={outputImageUrl}
                  alt="Output"
                  className="w-full h-24 object-cover rounded"
                  onError={(e) => {
                    e.currentTarget.style.display = "none";
                  }}
                />
              ) : (
                <div className="w-full h-24 bg-slate-700 rounded flex items-center justify-center text-slate-500 text-xs">
                  No output
                </div>
              )}
            </div>
          </div>
        )}

        {/* Click to enlarge hint */}
        {showImages && (inputImageUrl || outputImageUrl) && (
          <p className="text-xs text-slate-500 text-center mb-3">
            Click images to enlarge
          </p>
        )}

        {/* Stats */}
        {stats.length > 0 && (
          <div className="bg-slate-800/50 rounded-lg p-3">
            <h5 className="text-slate-300 text-xs font-semibold mb-2 uppercase tracking-wide">
              Results
            </h5>
            <div className="grid grid-cols-2 gap-2">
              {stats.map((stat, index) => (
                <div key={index} className="flex justify-between">
                  <span className="text-slate-400 text-sm">{stat.label}:</span>
                  <span className="text-white text-sm font-medium">{stat.value}</span>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Timestamp */}
        {job.completed_at && (
          <p className="text-xs text-slate-500 mt-2 text-right">
            Completed: {new Date(job.completed_at).toLocaleString()}
          </p>
        )}
      </div>

      {/* Image Modal */}
      {isModalOpen && inputImageUrl && outputImageUrl && (
        <ImageComparisonModal
          inputImage={inputImageUrl}
          outputImage={outputImageUrl}
          jobNumber={String(job.job_number || '')}
          onClose={() => setIsModalOpen(false)}
        />
      )}

    </>
  );
};
